import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { SubAdminModulePermissions } from '../client-sdk/api';
import { useLoginApi } from '../hooks/useLoginApi';

interface PermissionContextType {
  userType: string;
  permissions: SubAdminModulePermissions | null;
  hasPermission: (module: string, action: string) => boolean;
  hasAnyPermission: (module: string) => boolean;
  isSubAdmin: boolean;
  refreshPermissions: () => void;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  // const [userData, setUserData] = useState<any>(null);
  const [userType, setUserType] = useState<string>('');
  const [permissions, setPermissions] = useState<SubAdminModulePermissions | null>(null);
  const [isSubAdmin, setIsSubAdmin] = useState<boolean>(false);
  const { getSpecialUser } = useLoginApi();

  // Get user data from localStorage
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  };

  // Function to refresh permissions from localStorage
  const refreshPermissions = () => {
    const data = getUserData();
    // console.log('PermissionContext - Refreshing permissions with data:', data);
    // setUserData(data);
    setUserType(data?.userType || '');
    setPermissions(data?.userDetails?.permissions || null);
    setIsSubAdmin(data?.userType === 'subAdmin');
    // console.log('PermissionContext - Permissions refreshed. userType:', data?.userType, 'isSubAdmin:', data?.userType === 'subAdmin');
  };

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userData') {
        refreshPermissions();
      }
    };

    // Listen for storage events (when localStorage changes in other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Initial load
    const initializeSession = async () => {
      const data = getUserData();
      if (data?.userId && data?.userType) {
        try {
          const userDetails = await getSpecialUser({
            userId: data.userId,
            userType: data.userType,
          });
          const updatedUserData = { ...data, userDetails };
          updateUserData(updatedUserData);
        } catch (error) {
          console.error(
            'Failed to refresh user details on load, clearing session.',
            error
          );
          clearUserData();
        }
      } else {
        refreshPermissions();
      }
    };

    initializeSession();

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Also listen for custom events (for same-tab updates)
  useEffect(() => {
    const handleCustomStorageChange = () => {
      refreshPermissions();
    };

    window.addEventListener('userDataChanged', handleCustomStorageChange);

    return () => {
      window.removeEventListener('userDataChanged', handleCustomStorageChange);
    };
  }, []);

  // console.log('PermissionContext - Full userData:', userData);
  // console.log('PermissionContext - userType:', userType);
  // console.log('PermissionContext - isSubAdmin:', isSubAdmin);
  // console.log('PermissionContext - permissions:', permissions);
  // console.log('PermissionContext - choiceGroup permissions:', permissions?.choiceGroup);

  const hasPermission = (module: string, action: string): boolean => {
    // If not subadmin, allow all permissions (existing behavior) 
    if (!isSubAdmin) {
      // console.log(`PermissionContext - Not subadmin, allowing ${module}:${action}`);
      return true;
    }

    // If no permissions data, deny access
    if (!permissions) {
      // console.log(`PermissionContext - No permissions data, denying ${module}:${action}`);
      return false;
    }

    // Check if user has privilege (full access)
    if (permissions.privilege) {
      // console.log(`PermissionContext - User has privilege, allowing ${module}:${action}`);
      return true;
    }

    // Check specific module permissions
    const modulePermissions = permissions[module as keyof SubAdminModulePermissions];
    if (!modulePermissions) {
      // console.log(`PermissionContext - No module permissions for ${module}, denying ${action}`);
      return false;
    }

    // For SubAdminPermissions type (view, add, edit, delete, block, unBlock)
    if (typeof modulePermissions === 'object' && 'view' in modulePermissions) {
      // Normalize action -> permission keys. Default to lowercase action key,
      // with special handling for Unblock which the API may return as "unBlock".
      const actionLower = String(action || '').toLowerCase();
      const candidateKeys: string[] = [actionLower];
      if (actionLower === 'unblock') candidateKeys.push('unBlock');

      const hasPermission = candidateKeys.some((k) => (modulePermissions as any)[k] === true);
      // console.log(`PermissionContext - Checking ${module}:${action} -> ${permissionKey} = ${hasPermission}`);
      // console.log(`PermissionContext - Module permissions:`, modulePermissions);
      return hasPermission;
    }

    // For ViewOnlyPermission type (view only)
    if (typeof modulePermissions === 'object' && 'view' in modulePermissions && Object.keys(modulePermissions).length === 1) {
      const hasPermission = action === 'View' && modulePermissions.view === true;
      // console.log(`PermissionContext - ViewOnlyPermission check for ${module}:${action} = ${hasPermission}`);
      return hasPermission;
    }

    // For ViewAndEditOnlyPermission type (view, edit only)
    if (typeof modulePermissions === 'object' && 'view' in modulePermissions && 'edit' in modulePermissions) {
      const hasPermission = (action === 'View' || action === 'Edit') && modulePermissions[action === 'View' ? 'view' : 'edit'] === true;
      // console.log(`PermissionContext - ViewAndEditOnlyPermission check for ${module}:${action} = ${hasPermission}`);
      return hasPermission;
    }

    // console.log(`PermissionContext - No matching permission type for ${module}:${action}`);
    return false;
  };

  const hasAnyPermission = (module: string): boolean => {
    // If not subadmin, allow all permissions (existing behavior)
    if (!isSubAdmin) return true;

    // If no permissions data, deny access
    if (!permissions) return false;

    // Check if user has privilege (full access)
    if (permissions.privilege) return true;

    // Check specific module permissions
    const modulePermissions = permissions[module as keyof SubAdminModulePermissions];
    if (!modulePermissions) return false;

    // Only return true if the 'view' permission is true
    if (typeof modulePermissions === 'object' && 'view' in modulePermissions) {
      return modulePermissions['view'] === true;
    }

    return false;
  };

  const value: PermissionContextType = {
    userType,
    permissions,
    hasPermission,
    hasAnyPermission,
    isSubAdmin,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Utility function to update userData and trigger permission refresh
export const updateUserData = (userData: any) => {
  localStorage.setItem('userData', JSON.stringify(userData));
  window.dispatchEvent(new CustomEvent('userDataChanged'));
};

// Utility function to clear userData and trigger permission refresh
export const clearUserData = () => {
  localStorage.removeItem('userData');
  window.dispatchEvent(new CustomEvent('userDataChanged'));
}; 